// In-memory + localStorage демо-state. Включается когда IS_DEPLOYED=false.
// State хранится per-wallet-address (ключ: scrooge_mock_<addr>).
// Подписчики получают уведомления через `listeners`.
// BigInt сериализуем как строку с суффиксом "n" (scheme v1).

import { MIN_STAKE_WEI, PROTOCOL_FEE_BPS, BPS_DENOM } from './constants';

const ZERO = '0x0000000000000000000000000000000000000000';
const SCHEME_VERSION = 1;

function key(addr) {
  return `scrooge_mock_${String(addr || 'anon').toLowerCase()}`;
}

function replacer(_k, v) {
  if (typeof v === 'bigint') return { __b: v.toString() };
  return v;
}
function reviver(_k, v) {
  if (v && typeof v === 'object' && v.__b !== undefined) return BigInt(v.__b);
  return v;
}

function defaultState() {
  return {
    version: SCHEME_VERSION,
    usdtBalance: 0n,
    allowance: 0n,
    deposits: [], // { amount:bigint, planId:number, startedAt:number, lastClaimAt:number, active:bool }
    userInfo: {
      sponsor: ZERO,
      activeDepositCount: 0,
      firstDepositAt: 0,
      pendingReferral: 0n,
    },
    events: [], // { type, timestamp, hash, args:{...}, blockNumber, logIndex }
  };
}

function load(addr) {
  try {
    const raw = localStorage.getItem(key(addr));
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw, reviver);
    if (!parsed || parsed.version !== SCHEME_VERSION) return defaultState();
    return parsed;
  } catch {
    return defaultState();
  }
}

function save(addr, state) {
  try {
    localStorage.setItem(key(addr), JSON.stringify(state, replacer));
  } catch {
    /* private mode / quota */
  }
}

const listeners = new Set();
function notify() {
  for (const l of listeners) l();
}

// Возвращает snapshot. Кэшируем, чтобы useSyncExternalStore не триггерил бесконечные рендеры.
const cache = new Map();
function snapshot(addr) {
  const k = key(addr);
  if (!cache.has(k)) cache.set(k, load(addr));
  return cache.get(k);
}
function refresh(addr) {
  cache.set(key(addr), load(addr));
}
function mutate(addr, fn) {
  const s = load(addr);
  const next = fn(s) ?? s;
  save(addr, next);
  cache.set(key(addr), next);
  notify();
}

function randomHash() {
  const chars = '0123456789abcdef';
  let h = '0x';
  for (let i = 0; i < 64; i++) h += chars[Math.floor(Math.random() * 16)];
  return h;
}

function planDurationSec(plan) {
  if (!plan) return 0;
  if (plan.durationSeconds) return Number(plan.durationSeconds);
  return Number(plan.durationDays) * 86400;
}

function accruedNow(dep, plan, nowSec) {
  if (!dep.active || !plan) return 0n;
  const durSec = planDurationSec(plan);
  const endAt = Number(dep.startedAt) + durSec;
  const cap = Math.min(nowSec, endAt);
  const elapsed = Math.max(0, cap - Number(dep.lastClaimAt));
  if (elapsed === 0) return 0n;
  const amtBig = BigInt(dep.amount);
  const bpsBig = BigInt(plan.dailyBps);
  const eBig = BigInt(elapsed);
  return (amtBig * bpsBig * eBig) / (86400n * 10000n);
}

export const mockStore = {
  subscribe(l) {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  getSnapshot(addr) {
    return snapshot(addr);
  },

  // Получить сводку для useUserPortfolio
  getPortfolio(addr) {
    const s = snapshot(addr);
    const nowSec = Math.floor(Date.now() / 1000);
    return {
      userInfo: s.userInfo,
      deposits: s.deposits,
      usdtBalance: s.usdtBalance,
      allowance: s.allowance,
      pendingAll: s.deposits.reduce(
        (acc, d) => acc + (d.active ? accruedNow(d, null, nowSec) : 0n),
        0n,
      ),
      events: s.events,
    };
  },

  // ---- Actions ----

  creditUsdt(addr, amount) {
    mutate(addr, (s) => {
      s.usdtBalance = s.usdtBalance + BigInt(amount);
      s.events.unshift({
        type: 'UsdtCredited',
        timestamp: Math.floor(Date.now() / 1000),
        hash: randomHash(),
        blockNumber: BigInt(Math.floor(Date.now() / 3000)),
        logIndex: 0,
        args: { amount: BigInt(amount) },
      });
      return s;
    });
  },

  approve(addr, amount) {
    mutate(addr, (s) => {
      s.allowance = BigInt(amount);
      return s;
    });
  },

  deposit(addr, { amount, planId, referrer, plans }) {
    mutate(addr, (s) => {
      const amt = BigInt(amount);
      if (s.usdtBalance < amt) throw new Error('Insufficient USDT balance');
      if (s.allowance < amt) throw new Error('Allowance too low — approve first');
      const plan = plans?.[Number(planId)];
      if (!plan || !plan.active) throw new Error('Invalid or inactive strategy');
      const nowSec = Math.floor(Date.now() / 1000);
      const isFirst = s.deposits.length === 0;
      if (isFirst && referrer && referrer !== ZERO && referrer.toLowerCase() !== String(addr).toLowerCase()) {
        s.userInfo.sponsor = referrer;
      }
      if (isFirst) s.userInfo.firstDepositAt = nowSec;
      s.usdtBalance = s.usdtBalance - amt;
      s.allowance = s.allowance - amt;
      const idx = s.deposits.length;
      s.deposits.push({
        amount: amt,
        planId: Number(planId),
        startedAt: nowSec,
        lastClaimAt: nowSec,
        active: true,
      });
      s.userInfo.activeDepositCount += 1;
      s.events.unshift({
        type: 'Deposited',
        timestamp: nowSec,
        hash: randomHash(),
        blockNumber: BigInt(Math.floor(nowSec / 3)),
        logIndex: 0,
        args: { depositIdx: BigInt(idx), planId: Number(planId), amount: amt },
      });
      return s;
    });
  },

  claim(addr, depositIdx, plans) {
    mutate(addr, (s) => {
      const d = s.deposits[Number(depositIdx)];
      if (!d || !d.active) throw new Error('Position inactive');
      const plan = plans?.[Number(d.planId)];
      const nowSec = Math.floor(Date.now() / 1000);
      const gross = accruedNow(d, plan, nowSec);
      if (gross === 0n) throw new Error('Nothing to claim');
      const fee = (gross * BigInt(PROTOCOL_FEE_BPS)) / BigInt(BPS_DENOM);
      const net = gross - fee;
      d.lastClaimAt = nowSec;
      s.usdtBalance = s.usdtBalance + net;
      s.events.unshift({
        type: 'Claimed',
        timestamp: nowSec,
        hash: randomHash(),
        blockNumber: BigInt(Math.floor(nowSec / 3)),
        logIndex: 0,
        args: { depositIdx: BigInt(depositIdx), gross, fee, netToUser: net },
      });
      return s;
    });
  },

  reinvest(addr, srcIdx, targetPlanId, plans) {
    mutate(addr, (s) => {
      const d = s.deposits[Number(srcIdx)];
      if (!d || !d.active) throw new Error('Position inactive');
      const plan = plans?.[Number(d.planId)];
      const target = plans?.[Number(targetPlanId)];
      if (!target || !target.active) throw new Error('Invalid target strategy');
      const nowSec = Math.floor(Date.now() / 1000);
      const gross = accruedNow(d, plan, nowSec);
      const fee = (gross * BigInt(PROTOCOL_FEE_BPS)) / BigInt(BPS_DENOM);
      const net = gross - fee;
      if (net < MIN_STAKE_WEI) throw new Error('Net yield too low to reinvest');
      d.lastClaimAt = nowSec;
      const newIdx = s.deposits.length;
      s.deposits.push({
        amount: net,
        planId: Number(targetPlanId),
        startedAt: nowSec,
        lastClaimAt: nowSec,
        active: true,
      });
      s.userInfo.activeDepositCount += 1;
      s.events.unshift({
        type: 'Reinvested',
        timestamp: nowSec,
        hash: randomHash(),
        blockNumber: BigInt(Math.floor(nowSec / 3)),
        logIndex: 0,
        args: {
          srcDepositIdx: BigInt(srcIdx),
          newDepositIdx: BigInt(newIdx),
          newPlanId: Number(targetPlanId),
          gross,
          fee,
          reinvested: net,
        },
      });
      return s;
    });
  },

  withdraw(addr, depositIdx, plans) {
    mutate(addr, (s) => {
      const d = s.deposits[Number(depositIdx)];
      if (!d || !d.active) throw new Error('Position inactive');
      const plan = plans?.[Number(d.planId)];
      const nowSec = Math.floor(Date.now() / 1000);
      const endAt = Number(d.startedAt) + planDurationSec(plan);
      if (nowSec < endAt) throw new Error('Position still locked');
      // Auto-claim remaining yield
      const gross = accruedNow(d, plan, nowSec);
      const fee = (gross * BigInt(PROTOCOL_FEE_BPS)) / BigInt(BPS_DENOM);
      const net = gross - fee;
      d.lastClaimAt = nowSec;
      d.active = false;
      s.userInfo.activeDepositCount = Math.max(0, s.userInfo.activeDepositCount - 1);
      const principal = BigInt(d.amount);
      s.usdtBalance = s.usdtBalance + principal + net;
      if (gross > 0n) {
        s.events.unshift({
          type: 'Claimed',
          timestamp: nowSec,
          hash: randomHash(),
          blockNumber: BigInt(Math.floor(nowSec / 3)),
          logIndex: 0,
          args: { depositIdx: BigInt(depositIdx), gross, fee, netToUser: net },
        });
      }
      s.events.unshift({
        type: 'Withdrawn',
        timestamp: nowSec,
        hash: randomHash(),
        blockNumber: BigInt(Math.floor(nowSec / 3)),
        logIndex: 0,
        args: { depositIdx: BigInt(depositIdx), principal },
      });
      return s;
    });
  },

  // Реинвест на matured позиции: закрывает старую (principal + net yield → не на кошелёк, а в новую
   // позицию targetPlanId). В одной атомарной операции:
   //   1) auto-claim остатка yield,
   //   2) withdrawn events,
   //   3) старая становится inactive,
   //   4) создаётся новая позиция с суммой principal + net,
   //   5) deposited event.
  reinvestFull(addr, srcIdx, targetPlanId, plans) {
    mutate(addr, (s) => {
      const d = s.deposits[Number(srcIdx)];
      if (!d || !d.active) throw new Error('Position inactive');
      const plan = plans?.[Number(d.planId)];
      const target = plans?.[Number(targetPlanId)];
      if (!target || !target.active) throw new Error('Invalid target strategy');
      const nowSec = Math.floor(Date.now() / 1000);
      const endAt = Number(d.startedAt) + planDurationSec(plan);
      if (nowSec < endAt) throw new Error('Position still locked');

      const gross = accruedNow(d, plan, nowSec);
      const fee = (gross * BigInt(PROTOCOL_FEE_BPS)) / BigInt(BPS_DENOM);
      const net = gross - fee;
      const principal = BigInt(d.amount);
      const total = principal + net;
      if (total < MIN_STAKE_WEI) throw new Error('Amount too low for reinvest');

      d.lastClaimAt = nowSec;
      d.active = false;
      s.userInfo.activeDepositCount = Math.max(0, s.userInfo.activeDepositCount - 1);
      if (gross > 0n) {
        s.events.unshift({
          type: 'Claimed',
          timestamp: nowSec,
          hash: randomHash(),
          blockNumber: BigInt(Math.floor(nowSec / 3)),
          logIndex: 0,
          args: { depositIdx: BigInt(srcIdx), gross, fee, netToUser: net },
        });
      }
      s.events.unshift({
        type: 'Withdrawn',
        timestamp: nowSec,
        hash: randomHash(),
        blockNumber: BigInt(Math.floor(nowSec / 3)),
        logIndex: 1,
        args: { depositIdx: BigInt(srcIdx), principal },
      });

      const newIdx = s.deposits.length;
      s.deposits.push({
        amount: total,
        planId: Number(targetPlanId),
        startedAt: nowSec,
        lastClaimAt: nowSec,
        active: true,
      });
      s.userInfo.activeDepositCount += 1;
      s.events.unshift({
        type: 'Deposited',
        timestamp: nowSec,
        hash: randomHash(),
        blockNumber: BigInt(Math.floor(nowSec / 3)),
        logIndex: 2,
        args: { depositIdx: BigInt(newIdx), planId: Number(targetPlanId), amount: total },
      });
      return s;
    });
  },

  claimReferral(addr) {
    mutate(addr, (s) => {
      const amount = s.userInfo.pendingReferral;
      if (amount === 0n) throw new Error('No pending rewards');
      s.userInfo.pendingReferral = 0n;
      s.usdtBalance = s.usdtBalance + amount;
      s.events.unshift({
        type: 'ReferralClaimed',
        timestamp: Math.floor(Date.now() / 1000),
        hash: randomHash(),
        blockNumber: BigInt(Math.floor(Date.now() / 3000)),
        logIndex: 0,
        args: { amount },
      });
      return s;
    });
  },

  updateSponsor(addr, newSponsor) {
    mutate(addr, (s) => {
      s.userInfo.sponsor = newSponsor;
      return s;
    });
  },

  reset(addr) {
    try {
      localStorage.removeItem(key(addr));
      cache.delete(key(addr));
      notify();
    } catch {
      /* ignore */
    }
  },

  _planDurationSec: planDurationSec,
  _accruedNow: accruedNow,
};
